/**
 * documentCache.js
 *
 * Framework-agnostic localStorage LRU cache for instant document rendering.
 *
 * A single localStorage key (`cache_documents_<projectShortName>`) holds a map
 * of `{ [documentId]: entry }`, where
 *   entry = { documentId, documentData, documentInfo, lastAccessedAt, updatedAt }
 *
 * Every localStorage access is wrapped in try/catch and degrades to a no-op so
 * this module never throws (private mode, quota, disabled storage, etc.).
 */

const PROJECT_SHORT_NAME = 'mindmap';
const CACHE_KEY = `cache_documents_${PROJECT_SHORT_NAME}`;
const MAX_CACHED_DOCUMENTS = 5;

// Legacy key names that may exist from earlier iterations. They are removed once
// on module load so they never linger or collide with the current schema.
const LEGACY_CACHE_KEYS = [
  'cache_documents',
  'documentCache',
  'mm_document_cache',
  'cache_documents_flow',
];

function safeGetMap() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (e) {
    return {};
  }
}

function safeSetMap(map) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(map));
  } catch (e) {
    // no-op: storage unavailable / quota exceeded
  }
}

// A renderable entry is one that actually carries the document content payload.
function isRenderable(entry) {
  return !!(entry && entry.documentData && Array.isArray(entry.documentData.nodes));
}

/**
 * Cheap, NON-mutating existence check. Safe to call during render.
 * Returns true only when a renderable entry exists for the id.
 */
export function hasCachedDocument(documentId) {
  if (!documentId) return false;
  try {
    const map = safeGetMap();
    return isRenderable(map[documentId]);
  } catch (e) {
    return false;
  }
}

/**
 * Returns the raw entry for the id, bumping `lastAccessedAt` (a read counts as
 * an access for LRU purposes) and persisting the bump. Returns null if absent.
 */
export function getCachedDocument(documentId) {
  if (!documentId) return null;
  try {
    const map = safeGetMap();
    const entry = map[documentId];
    if (!entry) return null;
    entry.lastAccessedAt = Date.now();
    map[documentId] = entry;
    safeSetMap(map);
    return entry;
  } catch (e) {
    return null;
  }
}

/**
 * Upsert an entry. Undefined fields fall back to the existing value so callers
 * can patch documentData and documentInfo independently. Refreshes timestamps
 * and enforces the LRU limit before persisting.
 */
export function setCachedDocument(documentId, { documentData, documentInfo } = {}) {
  if (!documentId) return;
  try {
    const map = safeGetMap();
    const existing = map[documentId] || {};
    const now = Date.now();

    map[documentId] = {
      documentId,
      documentData: documentData !== undefined ? documentData : existing.documentData,
      documentInfo: documentInfo !== undefined ? documentInfo : existing.documentInfo,
      lastAccessedAt: now,
      updatedAt: now,
    };

    enforceLru(map);
    safeSetMap(map);
  } catch (e) {
    // no-op
  }
}

// Evict least-recently-accessed entries until the map is within the limit.
function enforceLru(map) {
  const ids = Object.keys(map);
  if (ids.length <= MAX_CACHED_DOCUMENTS) return;

  ids.sort((a, b) => (map[a]?.lastAccessedAt || 0) - (map[b]?.lastAccessedAt || 0));

  const removeCount = ids.length - MAX_CACHED_DOCUMENTS;
  for (let i = 0; i < removeCount; i++) {
    delete map[ids[i]];
  }
}

/**
 * Returns `{ documentData, documentInfo }` for the id, or null. Counts as an
 * access (delegates to getCachedDocument, which bumps lastAccessedAt).
 */
export function getMergedCachedData(documentId) {
  const entry = getCachedDocument(documentId);
  if (!entry) return null;
  return {
    documentData: entry.documentData || null,
    documentInfo: entry.documentInfo || null,
  };
}

// One-time cleanup of legacy key names, executed when the module is first loaded.
(function cleanupLegacyKeys() {
  try {
    LEGACY_CACHE_KEYS.forEach((key) => {
      try {
        localStorage.removeItem(key);
      } catch (e) {
        // no-op
      }
    });
  } catch (e) {
    // no-op
  }
})();
